import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import Sidebar from './Sidebar';
import BrandMark from '../BrandMark';
import { Toaster } from '../ui/sonner';

const AdminLayout = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user is authenticated (for now, check localStorage)
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token && location.pathname !== '/admin/login') {
      navigate('/admin/login');
    }
  }, [navigate, location]);

  // Close the mobile drawer on route change
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (_) { /* ignore */ }
    }
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminAccessToken');
    localStorage.removeItem('adminRefreshToken');
    localStorage.removeItem('adminEmail');
    navigate('/admin/login');
  };

  // Don't show sidebar on login page
  if (location.pathname === '/admin/login') {
    return (
      <div className="min-h-screen bg-slate-950">
        <Outlet />
        <Toaster position="top-right" richColors />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Sidebar (desktop + mobile drawer) */}
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        onLogout={handleLogout}
        isMobileOpen={isMobileOpen}
        onMobileClose={() => setIsMobileOpen(false)}
      />

      {/* Mobile backdrop */}
      {isMobileOpen && (
        <button
          aria-label="Close menu"
          onClick={() => setIsMobileOpen(false)}
          className="md:hidden fixed inset-0 bg-black/60 z-[55]"
          data-testid="admin-mobile-backdrop"
        />
      )}

      {/* Main Content */}
      <div
        className={`min-h-screen transition-all duration-300 ${
          isSidebarCollapsed ? 'md:ml-20' : 'md:ml-64'
        }`}
      >
        {/* Mobile top bar */}
        <header className="md:hidden sticky top-0 z-50 flex items-center justify-between h-14 px-4 bg-slate-900/95 backdrop-blur border-b border-slate-800">
          <button
            onClick={() => setIsMobileOpen(true)}
            className="p-2 -ml-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800"
            aria-label="Open menu"
            data-testid="admin-mobile-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </button>
          <a href="/admin/dashboard" className="flex items-center" data-testid="admin-mobile-brand">
            <BrandMark size="sm" showText={true} />
          </a>
          <div className="w-10" />
        </header>

        <main className="p-4 md:p-8 max-w-full overflow-x-hidden">
          <Outlet />
        </main>
      </div>

      <Toaster position="top-right" richColors />
    </div>
  );
};

export default AdminLayout;
